import { useEffect, useState } from "react";
import { FileText, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errorText, downloadFile } from "@/lib/api";
import { MONTHS } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export const FlashReportButton = ({ projectId, months, year }) => {
  const [month, setMonth] = useState(months[months.length - 1]);
  const [busy, setBusy] = useState("");
  const [summary, setSummary] = useState(null);
  const { can } = useAuth();
  useEffect(() => {
    api.get(`/projects/${projectId}/flash-report`, { params: { month } }).then((r) => setSummary(r.data.summary)).catch(() => setSummary(null));
  }, [projectId, month]);
  if (!months.length) return null;
  const dl = async () => {
    setBusy("pdf");
    try {
      await downloadFile(`/projects/${projectId}/flash-report/pdf?month=${month}`, `flash-report-${year}-${month}.pdf`);
      toast.success(`Flash report ${MONTHS[month - 1]} ${year} downloaded`);
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };
  const gen = async () => {
    setBusy("ai");
    try {
      const { data } = await api.post(`/projects/${projectId}/flash-report/summary`, null, { params: { month }, timeout: 120000 });
      setSummary(data);
      toast.success("AI executive summary generated");
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(""); }
  };
  return (
    <div className="mt-3" data-testid="flash-report-controls">
      <div className="flex flex-wrap items-center gap-2">
        <select value={month} onChange={(e) => setMonth(Number(e.target.value))} data-testid="flash-report-month-select" className="h-8 rounded-md bg-white text-slate-900 px-2 text-xs">
          {MONTHS.map((m, i) => <option key={m} value={i + 1} disabled={!months.includes(i + 1)}>{m} {year}{months.includes(i + 1) ? "" : " · no actuals"}</option>)}
        </select>
        {can("edit") && <Button size="sm" variant="outline" onClick={gen} disabled={busy !== ""} className="h-8 border-emerald-400/60 bg-transparent text-emerald-200 hover:bg-white/10 hover:text-white" data-testid="flash-summary-generate-button">
          <Sparkles size={13} className="mr-1" /> {busy === "ai" ? "Writing..." : summary ? "Regenerate AI summary" : "Generate AI summary"}
        </Button>}
        {can("export") && <Button size="sm" onClick={dl} disabled={busy !== ""} className="h-8 bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="flash-report-download-button">
          <FileText size={13} className="mr-1" /> {busy === "pdf" ? "Preparing..." : "Flash Report PDF"}
        </Button>}
      </div>
      {summary && (
        <div className="mt-3 rounded-md bg-white/5 border border-white/10 p-3 max-h-40 overflow-auto" data-testid="flash-summary-text">
          <p className="text-[10px] uppercase tracking-wider text-emerald-300 inline-flex items-center gap-1"><Sparkles size={11} /> Executive summary · {MONTHS[month - 1]} {year}</p>
          {summary.text.split("\n").filter((x) => x.trim()).map((x, i) => <p key={i} className="text-xs text-slate-200 leading-relaxed mt-1.5">{x}</p>)}
          <p className="text-[10px] text-slate-400 mt-2 font-mono">{summary.model} · {summary.by} · {new Date(summary.at).toLocaleString("en-GB")}</p>
        </div>
      )}
    </div>
  );
};
